/**
 * SmartSelectionChip — floats a "✨ Summarize" / "✨ Rewrite" chip when
 * the user highlights text anywhere on the site. Mounted once at the
 * app shell.
 *
 * Behaviour:
 *   • Only appears when selection length ≥ 40 chars (otherwise it's
 *     noisy for accidental double-clicks).
 *   • Hides on click, scroll, or key press.
 *   • The chip's actions currently deep-link to the Smart Chat with the
 *     selection pre-filled — no autonomous LLM call, so no cost /
 *     no runaway budget.
 *   • Suppressed inside contenteditable, input, textarea to not fight
 *     the browser's own selection UI.
 *   • Respects `prefers-reduced-motion` (skips the entrance transform).
 *   • Suppressed on /admin/* routes to keep admin UI monotone.
 */
import { useEffect, useState, useRef } from "react";
import { Sparkles, MessageSquare, Pencil } from "lucide-react";
import "./SmartAccent.css";

const MIN_CHARS = 40;
const HIDE_ROUTE_PREFIXES = ["/admin", "/login", "/signup", "/dashboard"];

function isInsideEditable(node) {
  let el = node && node.nodeType === 1 ? node : node?.parentElement;
  while (el && el !== document.body) {
    if (el.isContentEditable) return true;
    const tag = (el.tagName || "").toLowerCase();
    if (tag === "input" || tag === "textarea" || tag === "select") return true;
    el = el.parentElement;
  }
  return false;
}

export default function SmartSelectionChip() {
  const [pos, setPos] = useState(null); // {top, left, text}
  const timer = useRef(null);

  useEffect(() => {
    const check = () => {
      const path = window.location.pathname || "/";
      if (HIDE_ROUTE_PREFIXES.some((p) => path.startsWith(p))) {
        setPos(null); return;
      }
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed || !sel.rangeCount) { setPos(null); return; }
      const text = sel.toString().trim();
      if (text.length < MIN_CHARS) { setPos(null); return; }
      if (isInsideEditable(sel.anchorNode)) { setPos(null); return; }
      const range = sel.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      if (!rect || (rect.width === 0 && rect.height === 0)) { setPos(null); return; }
      // Position the chip BELOW the selection by default (an 8px gap
      // clears descenders). This avoids the chip landing on top of the
      // section heading that always sits above a selected paragraph.
      // Fall back to ABOVE only when there's not enough room below the
      // viewport (last 80px), so it never gets clipped off-screen.
      const CHIP_H = 40;
      const GAP    = 8;
      const spaceBelow = window.innerHeight - rect.bottom;
      const placeBelow = spaceBelow > CHIP_H + GAP + 12;
      const top = placeBelow
        ? rect.bottom + window.scrollY + GAP
        : Math.max(8, rect.top + window.scrollY - CHIP_H - GAP);
      // Clamp horizontally so the chip never overflows off the sides.
      const clampedLeft = Math.min(
        Math.max(rect.left + window.scrollX + rect.width / 2, 120),
        window.innerWidth - 120
      );
      setPos({ top, left: clampedLeft, text });
    };

    const onSelectionChange = () => {
      if (timer.current) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(check, 120);
    };
    const onHide = () => setPos(null);

    document.addEventListener("selectionchange", onSelectionChange);
    window.addEventListener("scroll", onHide, true);
    window.addEventListener("keydown", onHide);
    window.addEventListener("resize", onHide);
    return () => {
      document.removeEventListener("selectionchange", onSelectionChange);
      window.removeEventListener("scroll", onHide, true);
      window.removeEventListener("keydown", onHide);
      window.removeEventListener("resize", onHide);
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, []);

  if (!pos) return null;

  const openCopilot = (mode) => {
    // Deep-link into the existing Smart Chat / Copilot Hub with the
    // selection pre-filled. Non-authed visitors land at /signup with
    // an autofill hint; authed users land in Copilot Hub.
    const q = encodeURIComponent(pos.text.slice(0, 400));
    const target = `/copilot?intent=${mode}&text=${q}`;
    window.location.href = target;
  };

  return (
    <div
      data-testid="smart-selection-chip"
      role="toolbar"
      aria-label="Smart actions"
      style={{
        position: "absolute",
        top: pos.top,
        left: pos.left,
        transform: "translate(-50%, 0)",
        zIndex: 90,
      }}
      onMouseDown={(e) => e.preventDefault() /* keep selection alive */}
      className="rounded-full bg-white border border-slate-200 shadow-lg flex items-center gap-1 p-1 smart-accent-ring whitespace-nowrap"
    >
      <button
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => openCopilot("summarize")}
        data-testid="smart-selection-summarize"
        className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full smart-accent-btn whitespace-nowrap"
      >
        <Sparkles className="w-3 h-3" /> Summarize
      </button>
      <button
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => openCopilot("rewrite")}
        data-testid="smart-selection-rewrite"
        className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full text-slate-700 hover:bg-slate-100 whitespace-nowrap"
      >
        <Pencil className="w-3 h-3" /> Rewrite
      </button>
      <button
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => openCopilot("ask")}
        data-testid="smart-selection-ask"
        className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full text-slate-700 hover:bg-slate-100 whitespace-nowrap"
      >
        <MessageSquare className="w-3 h-3" /> Ask Copilot
      </button>
    </div>
  );
}
